import { memo, useEffect, useState } from 'react';

import moment from 'moment';

import { BillingEmptyStateIllustration } from './billing-empty-state-illustration';
import { CreditMonthlyChart } from './credit-monthly-chart';
import { CreditUsage } from './credit-usage';
import { SeatCreditUsage } from './seat-credit-usage';
import { SeatPeriodChart } from './seat-period-chart';
import { PricingModel } from '../../../../../backend/src/types/types';
import { Skeleton } from '../../components/skeleton';
import { useDownloadMatterCreditStats } from '@/api/mutations/useDownloadMatterCreditStats';
import { useFetchOrgUsageData } from '@/api/queries/analyticsQueries/useFetchOrgUsageData';
import { useFetchOrgUsers } from '@/api/queries/analyticsQueries/useFetchOrgUsers';
import { useFetchSeatBasedUsage } from '@/api/queries/analyticsQueries/useFetchSeatBasedUsage';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { Icon } from '@/new/components/icon';
import { SearchableCombobox } from '@/new/components/searchable-combobox';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/new/components/shadcn/empty';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';

// How many months of credit history the chart can show.
const MONTH_RANGE_OPTIONS = [3, 6, 12, 24];
const DEFAULT_MONTHS = 12;

// Credit tab — the org's credit usage. A bar chart of monthly credits; clicking a month
// reveals that month's per-matter breakdown.
export const CreditTab = memo(function CreditTab({
  organisationId,
  isViewingOtherOrg,
}: {
  organisationId: string;
  isViewingOtherOrg: boolean;
}) {
  // Only platform usage operators may fetch arbitrary organisation details. Regular users use
  // the seat-usage response itself to distinguish seat-based from usage-based billing.
  const { data: organisation, isLoading: isOrganisationLoading } = useGetOrganisation(
    isViewingOtherOrg ? organisationId : undefined,
  );
  const selectedOrganisationIsSeatBased = organisation?.pricingModel === PricingModel.SeatBased;
  // The usage view (monthly chart + per-matter breakdown + total) is shown for every org that
  // isn't seat-based — including orgs with no billing period or credit limit configured. The
  // backend reports usage from the usage rows regardless of whether a limit exists.
  // Seat usage is per user, so a platform operator viewing another seat-based org must pick a user first.
  const needsUserSelection = selectedOrganisationIsSeatBased && isViewingOtherOrg;
  const [selectedUserId, setSelectedUserId] = useState('');
  const [monthsToShow, setMonthsToShow] = useState(DEFAULT_MONTHS);
  useEffect(() => {
    setSelectedUserId('');
    setMonthsToShow(DEFAULT_MONTHS);
  }, [organisationId]);

  const { data: orgUsers } = useFetchOrgUsers(organisationId, needsUserSelection);
  const userOptions = (orgUsers ?? [])
    .map((orgUser) => ({ label: orgUser.email, value: orgUser.id }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const { data: seatBasedUsage, isLoading: isSeatLoading } = useFetchSeatBasedUsage(
    !isViewingOtherOrg || (selectedOrganisationIsSeatBased && selectedUserId !== ''),
    needsUserSelection ? organisationId : undefined,
    needsUserSelection && selectedUserId !== '' ? selectedUserId : undefined,
  );
  const isSeatBasedOrg = isViewingOtherOrg
    ? selectedOrganisationIsSeatBased
    : seatBasedUsage !== undefined && seatBasedUsage !== null;
  const isBillingModelLoading = isViewingOtherOrg ? isOrganisationLoading : isSeatLoading;
  const showUsageView = !isBillingModelLoading && !isSeatBasedOrg;

  // Headline credit usage — the org's total (plus limit/cycle when one is configured).
  const { data: usageData, isLoading: isUsageLoading } = useFetchOrgUsageData(showUsageView, organisationId);

  // Monthly chart over the last N months (independent of the billing cycle).
  const rangeStart = moment
    .utc()
    .subtract(monthsToShow - 1, 'months')
    .startOf('month')
    .toDate();
  const rangeEnd = moment.utc().endOf('month').toDate();
  const { data: chartData, isFetching: isChartFetching } = useFetchOrgUsageData(showUsageView, organisationId, {
    startDate: rangeStart,
    endDate: rangeEnd,
  });

  // Download the per-matter credit breakdown across the whole selected range.
  const { fetchDownload, isLoading: isDownloading } = useDownloadMatterCreditStats();

  if (isBillingModelLoading) {
    return (
      <div className="my-8 p-2">
        <Skeleton className="mt-4 block h-9 w-40 bg-gray-100" />
        <Skeleton className="mt-4 block h-2 w-full rounded-full bg-gray-100" />
      </div>
    );
  }

  return (
    <div className="my-6">
      <div className="ml-2">
        <h2 className="text-lg font-semibold text-gray-900">Credits</h2>
        <p className="mt-0.5 text-sm text-gray-500">
          {isSeatBasedOrg
            ? 'View your credit usage across your billing periods.'
            : "View your organisation's credit usage across your billing cycle."}
        </p>
      </div>

      {isSeatBasedOrg ? (
        <>
          {needsUserSelection && (
            <div className="mt-6 flex items-center justify-between gap-4 px-2">
              <p className="text-sm text-gray-500">
                This organisation is on seat-based pricing, so credit usage is tracked per user.
              </p>
              <SearchableCombobox
                icon="lucide--user"
                options={userOptions}
                value={selectedUserId}
                onChange={setSelectedUserId}
                placeholder="Select a user"
                searchPlaceholder="Search users..."
                emptyText="No users found"
                triggerClassName="w-64 justify-between font-normal"
              />
            </div>
          )}
          {needsUserSelection && selectedUserId === '' ? (
            <Empty className="py-24">
              <EmptyHeader className="max-w-md">
                <BillingEmptyStateIllustration />
                <EmptyTitle>Select a user</EmptyTitle>
                <EmptyDescription>
                  Credit usage for this organisation is tracked per user. Select a user above to view their usage.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <>
              <div className="my-8 p-2">
                <SeatCreditUsage
                  data={seatBasedUsage}
                  isLoading={isSeatLoading}
                  viewingOtherUser={needsUserSelection}
                />
              </div>
              {seatBasedUsage && seatBasedUsage.periods.length > 0 && <SeatPeriodChart data={seatBasedUsage} />}
            </>
          )}
        </>
      ) : (
        <>
          <div className="my-8 p-2">
            <CreditUsage periodCreditUsage={usageData?.periodCreditUsage ?? null} isLoading={isUsageLoading} />
          </div>

          <div className="mb-2 flex items-center justify-end gap-2 px-4">
            <Select value={String(monthsToShow)} onValueChange={(v) => setMonthsToShow(parseInt(v, 10))}>
              <SelectTrigger className="w-40 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_RANGE_OPTIONS.map((months) => (
                  <SelectItem key={months} value={String(months)}>
                    Last {months} months
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="primary"
              disabled={isDownloading}
              onClick={() => fetchDownload({ organisationId, startDate: rangeStart, endDate: rangeEnd })}
            >
              <Icon className={isDownloading ? 'lucide--loader-circle animate-spin' : 'lucide--download'} />
              Download
            </Button>
          </div>

          <CreditMonthlyChart
            monthlyUsage={chartData?.periodCreditUsage?.monthlyUsage ?? []}
            organisationId={organisationId}
            isFetching={isChartFetching}
          />
        </>
      )}
    </div>
  );
});
